import {
    useCallback, useContext, useMemo, useRef, useEffect,
} from 'react';
import { Button, Col, Row } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from '@s20/hooks/redux.hooks';
import { getTenantSettings } from '@s20/selectors/features/common.selectors';
import { Link } from '@s20/components/common/Link';
import { getDayName, getTimeZoneAbbr } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { OrganizationContactSelect } from '../../../organization/OrganizationContactSelect';
import { ApiRoutes, AppNavPaths } from '../../../Common';
import {
    FlexColumnStart,
    FormCircularProgress, FormContext, Input, Textarea,
} from '../../../common/forms/FormElements';
import { getShiftDifferentialWarning } from '../../../job/common';
import AddressFormNew from '../../../address/AddressFormNew';
import { Address } from '../../../address/Address';
import ValidatedSelect from '../../../common/forms/ValidatedSelect';
import { Subcontractor } from './Subcontractor';
import { JobType } from './JobType';
import { LocationSelector } from './LocationSelector';
import { Attachments } from './Attachments';

export const Job = () => {
    const { values, setFieldValue } = useContext(FormContext);
    const {
        companyId, contractId, dispatchOfficeLocationId,
    } = values;

    const addressFormRef = useRef(null);
    const prevStartTimeRef = useRef(null);

    const { data: contract, isLoading: contractIsLoading } = useQuery({ queryKey: ['contract', contractId], queryFn: () => httpClient.js(ApiRoutes.contract.byId(contractId)) });
    const { data: dispatchOffice, isLoading: dispatchOfficeIsLoading } = useQuery({ queryKey: ['company', dispatchOfficeLocationId], queryFn: () => httpClient.js(ApiRoutes.company.byId(dispatchOfficeLocationId)) });
    const { data: dispatchOffices } = useQuery({ queryKey: ['dispatchOffices'], queryFn: () => httpClient.js(ApiRoutes.typeAheads.dispatchLocations()) });

    const { data: usCounties, isLoading: countiesLoading } = useQuery({
        queryKey: ['usCounties'],
        queryFn: () => httpClient.js(ApiRoutes.USCounties.all()),
    });

    const tenantSettings = useSelector(getTenantSettings);

    const tzAbbreviation = useMemo(() => getTimeZoneAbbr(values.startTimeOffset, values.jobLocation?.timeZone, values.startTime, values.startTime), [values.startTimeOffset, values.jobLocation?.timeZone, values.startTime]);
    const isQuickRental = useMemo(() => values.isQuickRental, [values]);

    const shiftDifferentialWarning = useMemo(() => getShiftDifferentialWarning({
        currentDispatchCompany: dispatchOffice,
        tenantSettings,
        job: {
            ...values,
            contract,
        },
    }), [values, contract, tenantSettings, dispatchOffice]);

    useEffect(() => {
        if (!isQuickRental || !dispatchOffices) return;

        const dispatchLoc = dispatchOffices.find((d) => d.value === dispatchOfficeLocationId);
        if (!dispatchLoc) return;

        const dispatchAddress = Object.assign(new Address(), {
            address1: dispatchLoc.address1 ?? '',
            city: dispatchLoc.city ?? '',
            state: dispatchLoc.state ?? '',
            zip: dispatchLoc.zip ?? '',
            countryCode: dispatchLoc.countryCode ?? '',
            usStateId: dispatchLoc.uSStateId ?? null,
            countryId: dispatchLoc.countryId ?? null,
            timeZone: dispatchLoc.timeZone ?? null,
            usCountyId: dispatchLoc.uSCountyId ?? null,
            latitude: dispatchLoc.latitude ?? null,
            longitude: dispatchLoc.longitude ?? null,
        });

        setFieldValue('meetingLocation', dispatchAddress);
        setFieldValue('jobLocation', dispatchAddress);
    }, [isQuickRental, dispatchOffices, dispatchOfficeLocationId, setFieldValue]);

    const handleSaveAddress = useCallback(({ name, ...address }) => {
        if (isQuickRental) {
            setFieldValue('meetingLocation', address);
            setFieldValue('jobLocation', address);
            setFieldValue('meetingLocation_set', 'true');
            setFieldValue('jobLocation_set', 'true');
        } else {
            setFieldValue(name, address);
            setFieldValue(`${name}_set`, 'true');

            if (address.duplicate === true && address.otherFieldName) {
                setFieldValue(address.otherFieldName, {
                    ...address,
                    id: values[address.otherFieldName]?.id ?? address.id,
                });
                setFieldValue(`${address.otherFieldName}_set`, 'true');
            }
        }
    }, [setFieldValue, isQuickRental, values]);

    // Prefetch data
    useQuery({
        queryKey: ['resourceKits'],
        queryFn: () => httpClient.js(ApiRoutes.resourceKit.byTenant()),
    });

    // Prefetch data
    useQuery({
        queryKey: ['contractResources', contractId],
        queryFn: () => httpClient.js(ApiRoutes.job.getResources(contractId)),
    });

    const handleSelectProject = useCallback((project) => {
        if (project && project.id !== -1) {
            setFieldValue('requestedById', project.requestedById);
            setFieldValue('foremenId', project.foremanId);
            setFieldValue('workOrderNumber', project.workOrderNumber);
            setFieldValue('jobTypeId', project.jobTypeId);
            setFieldValue('subcontractorId', project.subcontractorId);
            setFieldValue('invoicedDate', project.lastInvoiced);
        }
    }, [setFieldValue]);

    const projects = useMemo(() => [
        { id: -1, projectNumber: '+ Create New Project' },
        ...(contract?.projects || [])
            .filter((project) => !project.isClosed)
            .map((project) => ({
                ...project,
                projectNumber: `${contract.number}-${project.contractSequence}`,
            })),
    ], [contract?.projects, contract?.number]);

    const handleStartTimeChange = useCallback((e) => {
        const newTime = e.target.value;

        if (newTime && newTime !== prevStartTimeRef.current && !isEmpty(values.resources)) {
            const newDayName = getDayName(newTime);
            const prevDayName = getDayName(prevStartTimeRef.current);

            const updatedResources = values.resources.map((r) => {
                const resourceValue = r[prevDayName];

                return {
                    ...r,
                    [prevDayName]: 0,
                    [newDayName]: resourceValue,
                };
            });

            setFieldValue('resources', updatedResources);
        }

        prevStartTimeRef.current = newTime;
    }, [values.resources, setFieldValue]);

    if (contract?.requiresJobApproval) {
        return (
            <>
                <h1>Contract Requires Job Approvals</h1>
                <p>
                    The selected contract requires pre-job approvals and needs to be created through the traditional job form.
                </p>

                <Link
                    component={Button}
                    size="lg"
                    to={`${AppNavPaths.JobNew}/${contractId}`}
                >
                    Create Job

                    <FontAwesomeIcon
                        icon={faArrowUpRightFromSquare}
                        className="ml-2"
                    />
                </Link>
            </>
        );
    }

    if (contractIsLoading || dispatchOfficeIsLoading || countiesLoading) {
        return <FormCircularProgress />;
    }

    return (
        <>
            <Row>
                {(Boolean(contract.isProjectContract) || tenantSettings.useContractProjects === 2) && (
                    <Col xs={12}>
                        <ValidatedSelect
                            options={projects}
                            getOptionLabel={(option) => option.projectNumber}
                            getOptionValue={(option) => option.id}
                            label="Project"
                            name="projectId"
                            id="projectId"
                            onChange={handleSelectProject}
                            required
                            error="A project selection is required"
                            valueField="id"
                            formatOptionLabel={(opt, { context }) => {
                                if (context === 'menu') {
                                    return (
                                        <FlexColumnStart>
                                            <span>{opt.projectNumber}</span>
                                            {Boolean(opt.workOrderNumber) && (
                                                <small className="text-muted mt-1">
                                                    WO#: {opt.workOrderNumber}
                                                </small>
                                            )}
                                        </FlexColumnStart>
                                    );
                                }

                                return <span>{opt.projectNumber}</span>;
                            }}
                        />
                    </Col>
                )}

                <Col xs={isQuickRental ? 12 : 6}>
                    <OrganizationContactSelect
                        id="requestedBy"
                        name="requestedById"
                        label="Requested By"
                        companyId={companyId}
                        required
                        showAddButton
                    />

                </Col>

                {!isQuickRental && (
                    <Col xs={6}>
                        <OrganizationContactSelect
                            id="foremenId"
                            name="foremenId"
                            label="Foreman"
                            companyId={companyId}
                            required
                            showAddButton
                        />
                    </Col>
                )}

                {contract.requireProjectInformation && !isQuickRental && (
                    <>
                        <Col xs={6}>
                            <Input
                                label="Project Name"
                                id="projectName"
                                name="projectName"
                                required
                                error="Project Name is required."
                            />
                        </Col>

                        <Col xs={6}>
                            <OrganizationContactSelect
                                id="projectManagerId"
                                name="projectManagerId"
                                label="Project Manager"
                                companyId={companyId}
                                required
                                error="A Project Manager selection is required."
                                showAddButton
                            />
                        </Col>
                    </>
                )}

                {contract.invoiceHasInspectorName && !isQuickRental && (
                    <Col xs={6}>
                        <Input
                            label="Inspector Name"
                            id="inspectorName"
                            name="inspectorName"
                        />
                    </Col>
                )}
            </Row>

            {contract.subcontractorsApply && (
                <Row>
                    <Col xs={6}>
                        <Subcontractor contractId={contractId} />
                    </Col>
                </Row>
            )}

            <Row>
                <Col
                    xs={12}
                    md={6}
                >
                    <Input
                        label="Start Time"
                        id="startTime"
                        required
                        type="datetime-local"
                        name="startTime"
                        error="Start Time is required."
                        onChange={handleStartTimeChange}
                        helperText={shiftDifferentialWarning}
                        endAddonClassName="inset"
                        endAddon={(
                            <span className="text-muted">
                                {tzAbbreviation}
                            </span>
                        )}
                    />
                </Col>

                {!isQuickRental && (
                    <Col
                        xs={12}
                        md={6}
                    >
                        <JobType />
                    </Col>
                )}


                <Col
                    xs={12}
                    md={6}
                >
                    <Input
                        label="Work Order"
                        id="workOrderNumber"
                        name="workOrderNumber"
                        maxLength={44}
                    />
                </Col>

                <Col
                    xs={12}
                    md={6}
                >
                    <Textarea
                        label="Notes"
                        id="notes"
                        name="notes"
                        placeholder="Enter notes to field staff here."
                        maxLength="1000"
                        rows="5"
                    />
                </Col>
            </Row>

            <hr />

            <Row>
                {!isQuickRental && (

                    <Col
                        xs={12}
                        md={6}
                    >
                        <LocationSelector
                            name="meetingLocation"
                            label="Meeting Location"
                            usCounties={usCounties}
                            addressFormRef={addressFormRef}
                            otherFieldName="jobLocation"
                        />
                    </Col>
                )}

                <Col
                    xs={12}
                    md={6}
                >
                    <LocationSelector
                        name="jobLocation"
                        label="Job Location"
                        usCounties={usCounties}
                        addressFormRef={addressFormRef}
                        otherFieldName="meetingLocation"
                        allowSameAs={!isQuickRental}
                    />
                </Col>
            </Row>

            {Boolean(tenantSettings.jobUploadsEnabled) && (
                <Attachments />
            )}

            <AddressFormNew
                id="jobAddressForm"
                ref={addressFormRef}
                namePrefix=""
                contractUsesPrevailingWage={contract.prevailingWage || contract.requirePaySchedule}
                isUnionWork={contract.isUnionWork}
                contractId={contractId}
                workTypeId={contract.contractUnionWorkTypeId}
                countryId={contract.countryId}
                countryCode={contract.countryCode}
                onSaveCallback={handleSaveAddress}
                usCounties={usCounties}
                timeZone={dispatchOffice.timeZone}
            />
        </>
    );
};